import {
  BayLcgVcgTcgAndPairings,
  TGetContainersCgsResult,
} from "@tedivo/tedivo-bay-grid-pure";
import {
  IBayLevelData,
  ILidData,
  IMasterCGs,
  ISizeSummary,
  IVesselParts,
} from "open-vessel-definition";
import {
  IDeckSelectContainerCriteria,
  useSimpleDeckView,
} from "@tedivo/tedivo-bay-grid-core";

import { ITvdCloudFileMeta } from "../../../../app/stores/OVDJsonStore";
import { getMeasuresFromSvgFileAttributes } from "./createSideView";
import { getTranslation } from "../../../../app/i18/i18tn";
import globalStore from "../../../../app/stores/globalStore";

interface ICreateDeckViewProps {
  sizeSummary: ISizeSummary;
  baysData: IBayLevelData[];
  masterCGs: IMasterCGs;
  lidData: ILidData[];
  vesselPartsData: IVesselParts[];
  v3DParams?: ITvdCloudFileMeta["v3DParams"];
  lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings;
  containersWithPositions: TGetContainersCgsResult;
  editActionNode?: HTMLElement;
  selectContainerCriteria: IDeckSelectContainerCriteria;
  bayNumbersDoubleClickAction?: (ev: SVGElement | HTMLElement) => void;
}

export async function createDeckView({
  sizeSummary,
  baysData,
  masterCGs,
  lidData,
  lcgVcgTcgAndPairings,
  editActionNode,
  vesselPartsData,
  v3DParams,
  containersWithPositions,
  selectContainerCriteria,
  bayNumbersDoubleClickAction,
}: ICreateDeckViewProps): Promise<HTMLElement> {
  const root = getComputedStyle(document.body);

  const card = document.createElement("card-element");
  card.titleHtml = "view:deckView.title";
  card.name = "deckView";

  if (editActionNode) card.actions = editActionNode;

  try {
    if (lcgVcgTcgAndPairings.cgStats.totalSlotsCount === 0) {
      const empty = document.createElement("div");
      empty.className = "no-slots-defined";
      empty.innerHTML = getTranslation(
        "view:graphicWillDisplaySlotsWhenDefined",
      );
      card.appendChild(empty);
    }

    const svgSvg = await getDeckViewSvgElement();
    const svgElementMeasures = getMeasuresFromSvgFileAttributes(
      svgSvg,
      "#DeckView",
      {
        paddingShip: { x: 0, y: 0, z: 100 },
        paddingContArea: { x: 0, y: 0, z: 20 },
      },
    );

    const { deckViewSvg } = useSimpleDeckView({
      sizeSummary,
      baysData,
      masterCGs,
      lidData,
      bayLcvVcgTcgAndPairings: lcgVcgTcgAndPairings,
      symbolsOptions: {
        strokeWidth: 4,
        strokeColor: root.getPropertyValue("--sl-color-neutral-300"),
        dimmedStrokeColor: root.getPropertyValue("--sl-color-neutral-200"),
        fontColor: root.getPropertyValue("--sl-color-neutral-700"),
        fillColor: root.getPropertyValue("--sl-color-neutral-50"),
        shipStrokeColor: root.getPropertyValue("--sl-color-primary-300"),
        shipTopColor: v3DParams?.params?.colors?.mainColor,
        shipBottomColor: v3DParams?.params?.colors?.bottomColor,
        lidFillColor: root.getPropertyValue("--sl-color-green-200"),
        lidWarningFillColor: root.getPropertyValue("--sl-color-orange-200"),
        lidDangerFillColor: root.getPropertyValue("--sl-color-red-200"),
        lidTextColor: root.getPropertyValue("--sl-color-primary-700"),
        warningColor: root.getPropertyValue("--sl-color-warning-700"),
        skyColor: root.getPropertyValue("--sl-color-neutral-0"),
      },
      selectContainerCriteria,
      vesselPartsData,
      containersWithPositions,
      bayNumbersDoubleClickAction,
      svgElement: svgSvg,
      svgElementMeasures,
    });

    card.appendChild(deckViewSvg);

    deckViewSvg.setAttribute("id", "deckViewSvg");

    const legend = document.createElement("div");
    legend.className = "deckview-legend";

    const validOk = document.createElement("span");
    validOk.className = "deckview-legend-ok";
    validOk.innerHTML = getTranslation("view:lids.validation.ok");
    legend.appendChild(validOk);

    const validWarning = document.createElement("span");
    validWarning.className = "deckview-legend-warning";
    validWarning.innerHTML = getTranslation("view:lids.validation.warning");
    legend.appendChild(validWarning);

    const validDanger = document.createElement("span");
    validDanger.className = "deckview-legend-danger";
    validDanger.innerHTML = getTranslation("view:lids.validation.danger");
    legend.appendChild(validDanger);

    card.appendChild(legend);
  } catch (e) {
    console.error(e);
  }

  return card;
}

export async function getDeckViewSvgElement(): Promise<SVGSVGElement> {
  if (!globalStore.deckViewSvg) {
    const svgg = await fetch("/assets/images/Container-Ship-Top-View-V4_F.svg");

    const svggText = await svgg.text();
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svggText, "image/svg+xml");
    const svgSvg = svgDoc.documentElement as unknown as SVGSVGElement;
    globalStore.deckViewSvg = svgSvg;
    return svgSvg;
  } else {
    return globalStore.deckViewSvg;
  }
}
